import * as THREE from 'three';
import type { Colony } from '../sim/Colony';
import type { Terrain } from '../game/Terrain';
import type { NestConfig } from '../sim/contracts';

/** Max eggs (or larvae) rendered per colony. Capped to keep the pools small. */
const MAX_PER_POOL_PER_COLONY = 40;

/** Ring around each nest where brood clusters sit, in metres. */
const RING_MIN_RADIUS = 0.6;
const RING_MAX_RADIUS = 0.9;

/** Small Y lift so eggs/larvae aren't z-fighting with the crater terrain. */
const Y_LIFT = 0.004;

/**
 * Visible brood — eggs and larvae — clustered at the rim of each nest.
 * Driven by the Colony's `brood` array; placements are deterministic per
 * brood index + team so entries don't jitter between frames even though
 * the array is mutated by the sim.
 */
export class BroodRender {
  readonly group: THREE.Group;

  private readonly eggMesh: THREE.InstancedMesh;
  private readonly larvaMesh: THREE.InstancedMesh;
  private readonly eggCap: number;
  private readonly larvaCap: number;

  private readonly tmpMat = new THREE.Matrix4();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpScale = new THREE.Vector3();

  constructor(
    private readonly colonies: Colony[],
    private readonly nests: NestConfig[],
    private readonly terrain: Terrain,
  ) {
    this.group = new THREE.Group();
    this.group.name = 'brood-render';

    const perPool = MAX_PER_POOL_PER_COLONY * Math.max(1, colonies.length);
    this.eggCap = perPool;
    this.larvaCap = perPool;

    // --- Egg: small oval, stretched on X so it reads as an ovoid.
    const eggGeo = new THREE.SphereGeometry(0.01, 12, 8);
    eggGeo.scale(1.6, 1.0, 1.0);
    const eggMat = new THREE.MeshStandardMaterial({
      color: 0xf6efd0,
      roughness: 0.4,
      metalness: 0,
    });
    this.eggMesh = new THREE.InstancedMesh(eggGeo, eggMat, this.eggCap);
    this.eggMesh.count = 0;
    this.eggMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.eggMesh.castShadow = true;
    this.eggMesh.receiveShadow = true;
    this.eggMesh.frustumCulled = false;
    this.eggMesh.name = 'brood-eggs';

    // --- Larva: short curled capsule lying flat on the ground.
    const larvaGeo = new THREE.CapsuleGeometry(0.008, 0.025, 4, 8);
    // CapsuleGeometry aligns along +Y by default; rotate so it lies flat on +X.
    larvaGeo.rotateZ(Math.PI / 2);
    const larvaMat = new THREE.MeshStandardMaterial({
      color: 0xf6efd0,
      roughness: 0.5,
      metalness: 0,
    });
    this.larvaMesh = new THREE.InstancedMesh(larvaGeo, larvaMat, this.larvaCap);
    this.larvaMesh.count = 0;
    this.larvaMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.larvaMesh.castShadow = true;
    this.larvaMesh.receiveShadow = true;
    this.larvaMesh.frustumCulled = false;
    this.larvaMesh.name = 'brood-larvae';

    this.group.add(this.eggMesh);
    this.group.add(this.larvaMesh);
  }

  update(): void {
    let eggN = 0;
    let larvaN = 0;

    const colonyCount = Math.min(this.colonies.length, this.nests.length);
    for (let ci = 0; ci < colonyCount; ci++) {
      const colony = this.colonies[ci];
      const nest = this.nests[ci];
      const team = colony.team;

      const brood = colony.brood;
      const limit = Math.min(brood.length, MAX_PER_POOL_PER_COLONY * 2);

      for (let bi = 0; bi < limit; bi++) {
        const b = brood[bi];

        // Deterministic ring position: seed = brood index * prime + team.
        const seed = bi * 2654435761 + (team + 1) * 374761393;
        const ang = this.fract01(seed, 1) * Math.PI * 2;
        const rr = RING_MIN_RADIUS + this.fract01(seed, 2) * (RING_MAX_RADIUS - RING_MIN_RADIUS);
        const x = nest.x + Math.cos(ang) * rr;
        const z = nest.z + Math.sin(ang) * rr;
        const y = this.terrain.getHeight(x, z) + Y_LIFT;

        // Orient larvae along a tangent to the nest rim so the cluster looks
        // swept, not radially spiking. Eggs keep default orientation (they're
        // ovoids, the extra yaw barely reads).
        const yaw = ang + Math.PI / 2;
        this.tmpQuat.setFromAxisAngle(UP, yaw);
        this.tmpPos.set(x, y, z);
        this.tmpScale.set(1, 1, 1);
        this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);

        if (b.phase === 'egg') {
          if (eggN < this.eggCap) {
            this.eggMesh.setMatrixAt(eggN, this.tmpMat);
            eggN++;
          }
        } else {
          if (larvaN < this.larvaCap) {
            this.larvaMesh.setMatrixAt(larvaN, this.tmpMat);
            larvaN++;
          }
        }
      }
    }

    this.eggMesh.count = eggN;
    this.larvaMesh.count = larvaN;
    this.eggMesh.instanceMatrix.needsUpdate = true;
    this.larvaMesh.instanceMatrix.needsUpdate = true;
  }

  /** Deterministic pseudo-random in [0, 1) from a seed + stream index. */
  private fract01(seed: number, stream: number): number {
    let x = (seed ^ (stream * 0x9e3779b1)) >>> 0;
    x = Math.imul(x ^ (x >>> 15), 0x85ebca6b) >>> 0;
    x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35) >>> 0;
    x = (x ^ (x >>> 16)) >>> 0;
    return x / 0x100000000;
  }
}

const UP = new THREE.Vector3(0, 1, 0);
